import type { Props } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'

export const NATIVE_RANGE = 1000
type SegmentConfigEntry = [
    valueMin: number,
    valueMax: number,
    segmentEnd: number,
]
export type SegmentConfig = [
    SegmentConfigEntry,
    SegmentConfigEntry,
    ...SegmentConfigEntry[],
]
export type Segment = [
    valueMin: number,
    valueMax: number,
    segmentStart: number,
    segmentEnd: number,
]
export type SegmentConfigFinal = [Segment, Segment, ...Segment[]]

export type SegmentedProps = Props & {
    segmentsConfig: SegmentConfig
    step: number
    debounce: number
}

export function segmentsConfigToSegments(
    config: SegmentConfig
): SegmentConfigFinal {
    return config.map((segment, index, arr) => [
        segment[0],
        segment[1],
        arr[index - 1]?.[2] || 0,
        segment[2],
    ]) as SegmentConfigFinal
}

const getSegmentForRealValue = (
    value: number,
    config: SegmentConfigFinal,
    max: number
): Segment => {
    const segment = config.find((s) => value >= s[0] && value <= s[1])

    if (segment) {
        return segment
    }

    if (value >= max) {
        const segment = config[config.length - 1]

        if (segment) {
            return segment
        }
    }

    return config[0]
}

const calculateValueRatioInSegment = (value: number, segment: Segment) =>
    (value - segment[0]) / (segment[1] - segment[0])

const calculateNativeValueInSegment = (ratio: number, segment: Segment) =>
    NATIVE_RANGE * (segment[2] + (segment[3] - segment[2]) * ratio)

export const convertRealToNative = (
    value: number,
    config: SegmentConfigFinal,
    max: number
): number => {
    const segment = getSegmentForRealValue(value, config, max)

    // Account for values that do not fit the segment
    // (closest segment is returned if the value does not fit)
    const ratio = Math.min(1, calculateValueRatioInSegment(value, segment))

    return calculateNativeValueInSegment(ratio, segment)
}
